import { RowSelectionState } from '@tanstack/react-table';
import { create } from 'zustand';

import { getApprovalEntryRowId } from '../helpers/approval-row-model.helper';
import { invalidApprovedInputKey } from '../helpers/invalid-approved-inputs.helper';
import { getSelectionLevelForRowId } from '../helpers/table-selection.helpers';
import {
  ApprovedHoursField,
  EditedEntryValues,
  InvalidApprovedInput,
  InvalidApprovedInputs,
} from '../types/approvals-table.types';
import { ApprovalExclusion, ApprovalTimeEntry } from '../types/time-tracking-approvals';

/**
 * Only ONE selection level (projects / members / worklogs) may be active at a time —
 * the approve/unapprove API accepts a single level per request. When a toggle adds
 * row(s) of a new level, every previously selected row of another level is dropped.
 *
 * Deselected rows are always pruned, never kept as `false`: `Object.keys(rowSelection).length`
 * is what tells the page and the table "is anything selected?" (it drives the action footer),
 * so a leftover falsy key would show an empty footer with disabled buttons.
 */
const enforceSingleSelectionLevel = (
  prev: RowSelectionState,
  next: RowSelectionState
): RowSelectionState => {
  const selected = Object.fromEntries(Object.entries(next).filter(([, isSelected]) => isSelected));
  const addedIds = Object.keys(selected).filter(rowId => !prev[rowId]);
  if (addedIds.length === 0) return selected;

  const level = getSelectionLevelForRowId(addedIds[addedIds.length - 1]);
  return Object.fromEntries(
    Object.entries(selected).filter(([rowId]) => getSelectionLevelForRowId(rowId) === level)
  );
};

/**
 * An exclusion only makes sense while the selection it punches a hole in is still
 * active — drop every exclusion whose covering ancestor is no longer selected.
 */
const pruneOrphanedExclusions = (
  exclusions: Record<string, ApprovalExclusion>,
  rowSelection: RowSelectionState
): Record<string, ApprovalExclusion> => {
  const entries = Object.entries(exclusions).filter(
    ([, exclusion]) => rowSelection[exclusion.ancestorRowId]
  );
  if (entries.length === Object.keys(exclusions).length) return exclusions;
  return Object.fromEntries(entries);
};

interface ApprovalsState {
  rowSelection: RowSelectionState;
  /**
   * Rows explicitly unchecked underneath a selected project/member row, keyed by row id.
   * Snapshots (not row references) so they survive collapse/unload of the subtree.
   * Kept in sync with `rowSelection` — see `pruneOrphanedExclusions`.
   */
  exclusions: Record<string, ApprovalExclusion>;
  // Pending inline edits keyed by entry id. Held here (not in component state) so they
  // survive re-renders, lazy-tree re-derivation, cell remounts, and filter changes.
  editedEntryValues: Record<number, EditedEntryValues>;
  /**
   * The edited rows as they looked when first touched, keyed by entry id.
   *
   * A worklog-level request item needs the entry's `hash` and its ancestors, and collapsing a
   * section DROPS its rows from the loaded tree. Without this snapshot an aggregate approve
   * over a collapsed section silently sent no worklog items at all — approving every edited
   * row at its old tracked hours. Snapshots are why exclusions survive the same round trip.
   */
  editedEntrySnapshots: Record<number, ApprovalTimeEntry>;
  /**
   * Approved-hours inputs whose text currently doesn't parse.
   *
   * An unparseable input never commits, so approving the row would save its OLD value behind
   * the user's back. Holding this here (rather than in the input) is what lets the footer and
   * the row's approve button refuse, the member/project rows warn, and the typed text survive
   * the row being collapsed. Read it through `helpers/invalid-approved-inputs.helper`.
   */
  invalidApprovedInputs: InvalidApprovedInputs;

  setRowSelection: (
    selection: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
  clearRowSelection: () => void;

  /** Record an exclusion under a selected ancestor row. */
  addExclusion: (exclusion: ApprovalExclusion) => void;
  /** Remove exclusions by row id (re-checking a row). */
  removeExclusions: (rowIds: string[]) => void;
  /**
   * Atomically remove + add exclusions (e.g. converting a member exclusion into
   * per-entry exclusions when one of its entries is re-checked).
   */
  replaceExclusions: (removeRowIds: string[], add: ApprovalExclusion[]) => void;

  // Each takes the whole entry, not just its id: the snapshot taken here is what keeps the
  // edit reachable after its row unmounts.
  setApprovedForInvoiceHours: (entry: ApprovalTimeEntry, hours: number) => void;
  setApprovedForBillHours: (entry: ApprovalTimeEntry, hours: number) => void;
  setApprovalNotes: (entry: ApprovalTimeEntry, notes: string | null) => void;
  /** Remember an approved input as unparseable, together with the text the user typed. */
  markApprovedInputInvalid: (input: InvalidApprovedInput) => void;
  /** Forget an approved input once its text parses again. */
  clearApprovedInputInvalid: (entryId: number, field: ApprovedHoursField) => void;
  /** Drop pending edits and clear selection for the given entries (e.g. after approval). */
  clearEditedEntries: (entryIds: number[]) => void;
  clearAllEdits: () => void;
}

/** Merges one field into an entry's pending edit, leaving its other fields untouched. */
const withEdit = (
  editedEntryValues: Record<number, EditedEntryValues>,
  entryId: number,
  patch: EditedEntryValues
): Record<number, EditedEntryValues> => ({
  ...editedEntryValues,
  [entryId]: { ...editedEntryValues[entryId], ...patch },
});

/**
 * Records the row a pending edit belongs to. Kept from the FIRST edit: re-snapshotting on every
 * keystroke would be pointless churn, and the row's identity (hash, ancestors, tracked hours)
 * doesn't change while the user types.
 */
const withSnapshot = (
  snapshots: Record<number, ApprovalTimeEntry>,
  entry: ApprovalTimeEntry
): Record<number, ApprovalTimeEntry> =>
  snapshots[entry.id] ? snapshots : { ...snapshots, [entry.id]: entry };

/** Drops every record whose key maps to one of `entryIds`. */
const omitEntries = <T>(
  record: Record<string, T>,
  entryIds: Set<number>,
  entryIdOf: (value: T) => number
): Record<string, T> =>
  Object.fromEntries(Object.entries(record).filter(([, value]) => !entryIds.has(entryIdOf(value))));

export const useApprovalsStore = create<ApprovalsState>((set, get) => ({
  rowSelection: {},
  exclusions: {},
  editedEntryValues: {},
  editedEntrySnapshots: {},
  invalidApprovedInputs: {},

  setRowSelection: selection => {
    const prev = get().rowSelection;
    const next = enforceSingleSelectionLevel(
      prev,
      typeof selection === 'function' ? selection(prev) : selection
    );
    set({ rowSelection: next, exclusions: pruneOrphanedExclusions(get().exclusions, next) });
  },

  clearRowSelection: () => set({ rowSelection: {}, exclusions: {} }),

  addExclusion: exclusion =>
    set(state => ({ exclusions: { ...state.exclusions, [exclusion.rowId]: exclusion } })),

  removeExclusions: rowIds => {
    if (rowIds.length === 0) return;
    const idSet = new Set(rowIds);
    set(state => ({
      exclusions: Object.fromEntries(
        Object.entries(state.exclusions).filter(([rowId]) => !idSet.has(rowId))
      ),
    }));
  },

  replaceExclusions: (removeRowIds, add) => {
    const idSet = new Set(removeRowIds);
    set(state => ({
      exclusions: {
        ...Object.fromEntries(
          Object.entries(state.exclusions).filter(([rowId]) => !idSet.has(rowId))
        ),
        ...Object.fromEntries(add.map(exclusion => [exclusion.rowId, exclusion])),
      },
    }));
  },

  // Each setter patches ONE field: the invoice input, the bill input and the notes are three
  // separate controls, so committing one must never wipe another's pending edit.
  setApprovedForInvoiceHours: (entry, hours) =>
    set(state => ({
      editedEntryValues: withEdit(state.editedEntryValues, entry.id, {
        approvedForInvoiceHours: hours,
      }),
      editedEntrySnapshots: withSnapshot(state.editedEntrySnapshots, entry),
    })),

  setApprovedForBillHours: (entry, hours) =>
    set(state => ({
      editedEntryValues: withEdit(state.editedEntryValues, entry.id, {
        approvedForBillHours: hours,
      }),
      editedEntrySnapshots: withSnapshot(state.editedEntrySnapshots, entry),
    })),

  setApprovalNotes: (entry, notes) =>
    set(state => ({
      editedEntryValues: withEdit(state.editedEntryValues, entry.id, { approvalNotes: notes }),
      editedEntrySnapshots: withSnapshot(state.editedEntrySnapshots, entry),
    })),

  markApprovedInputInvalid: input =>
    set(state => {
      const key = invalidApprovedInputKey(input.entryId, input.field);
      // Every mounted input reports on each render pass; returning a fresh object when nothing
      // actually changed would re-render every subscriber of the store.
      if (state.invalidApprovedInputs[key]?.rawValue === input.rawValue) return state;
      return { invalidApprovedInputs: { ...state.invalidApprovedInputs, [key]: input } };
    }),

  clearApprovedInputInvalid: (entryId, field) =>
    set(state => {
      const key = invalidApprovedInputKey(entryId, field);
      if (!state.invalidApprovedInputs[key]) return state;

      const invalidApprovedInputs = { ...state.invalidApprovedInputs };
      delete invalidApprovedInputs[key];
      return { invalidApprovedInputs };
    }),

  clearEditedEntries: entryIds => {
    if (entryIds.length === 0) return;
    const idSet = new Set(entryIds);
    const rowIdSet = new Set(entryIds.map(getApprovalEntryRowId));
    set(state => {
      const rowSelection = Object.fromEntries(
        Object.entries(state.rowSelection).filter(([rowId]) => !rowIdSet.has(rowId))
      );
      return {
        editedEntryValues: Object.fromEntries(
          Object.entries(state.editedEntryValues).filter(([id]) => !idSet.has(Number(id)))
        ),
        editedEntrySnapshots: Object.fromEntries(
          Object.entries(state.editedEntrySnapshots).filter(([id]) => !idSet.has(Number(id)))
        ),
        // A committed entry can't still hold unparseable text — drop its inputs too, or the
        // footer would stay blocked by an input that no longer exists.
        invalidApprovedInputs: omitEntries(
          state.invalidApprovedInputs,
          idSet,
          input => input.entryId
        ),
        rowSelection,
        exclusions: pruneOrphanedExclusions(state.exclusions, rowSelection),
      };
    });
  },

  clearAllEdits: () =>
    set({ editedEntryValues: {}, editedEntrySnapshots: {}, invalidApprovedInputs: {} }),
}));
